import Contract from "slotworks/utils/contract";
import SpineSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/spine-symbol-behaviour";
import Point from "slotworks/utils/geom/point";
import AbstractSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/abstract-symbol-behaviour";
import spineManager from "slotworks/graphics/spine/spine-manager";
import Layers from "slotworks/graphics/layers/layers";
import ExtendedSpine from "slotworks/graphics/spine/spine";
import SymbolComponent from "slotworks/components/matrix/symbol/symbol-component";
import spinePool from "./spine-pool";
import deviceInfo from "slotworks/utils/device-info";
import canvasManager, { Orientation } from "slotworks/graphics/canvas/canvas-manager";


export default class HarleySpineSymbolBehaviour extends AbstractSymbolBehaviour {

    public static ANIMATE_BEHIND: boolean = false;

    protected masterSymbol: string = "symbol_1_1";

    protected defaultAnimation: string;

    protected spine: ExtendedSpine;

    protected offset: Point;

    protected completeResolve: () => void;

    /**
     * Animates spine over top of the static sprite during a win
     *
     * @param symbol {SymbolComponent}
     * @param [defaultAniamtion="win"] {string}
     */
    constructor(symbol: SymbolComponent) {
        super(symbol);
    }

    public static() {
        this.cleanUp();
    }

    public updateTransform(): void {
        super.updateTransform();
        if (this.spine) {
            const symbolTransform = this.symbol.getTransform();

            const globalPosition = Layers.MatrixContent.localToGlobal(symbolTransform);
            const localPosition = Layers.SymbolAnimations.globalToLocal(globalPosition);

            this.spine.x = localPosition.x + localPosition.width / 2 + this.offset.x;
            this.spine.y = localPosition.y + localPosition.height / 2 + this.offset.y;
        }
    }

    public land(...args: any[]): Contract<void> {
        return Contract.empty();
    }

    public win(...args: any[]): Contract<void> {
        this.symbol.setVisible(false);
        return this.animate("win", true, false);
    }

    public cycle(...args: any[]): Contract<void> {
        this.symbol.setVisible(false);
        return this.animate("win", true, false);
    }

    public highlight(...args: any[]): Contract<void> {
        this.symbol.setVisible(false);
        return this.animate("win", true, false);
    }

    public anticipate(...args: any[]): Contract<void> {
        return Contract.empty();
    }

    protected animate(animation: string, autoReset: boolean = false, loop: boolean = false, position: string = null, offset: Point = null, timeScale: number = undefined) {

        // Resolve existing animations before starting a new one
        if (this.completeResolve) {
            this.completeResolve();
        }
        this.cleanUp();

        const spine = spinePool.getSpineSymbol(this.symbol.symbolId); //spineManager.createSpineSymbol(this.symbol.symbolId);        
        if (!spine) {
            return Contract.empty();
        }

        spine.name = this.symbol.symbolId;
        spine.visible = true;
        this.spine = spine;

        if (timeScale) {
            // this.spine.state.timeScale *= 0.6;
            this.spine.state.timeScale = timeScale;            
        } else {
            this.spine.state.timeScale = 1;
        }

        const masterRect = Layers.MatrixContent.getPosition(this.masterSymbol);

        const animRect = position ? Layers.MatrixContent.getPosition(position) : null;

        if (offset) {
            this.offset = offset;
        } else if (animRect) {
            this.offset = new Point(
                animRect.x + animRect.width / 2 - masterRect.x - masterRect.width / 2,
                animRect.y + animRect.height / 2 - masterRect.y - masterRect.height / 2
            );
        } else {
            this.offset = new Point(0, 0);
            const offsets = canvasManager.orientation === Orientation.LANDSCAPE ? OFFSETS_LANDSCAPE : deviceInfo.widePortrait ? OFFSET_PORTRAITV2 : OFFSET_PORTRAIT;
            
            const offsetForSymbol = offsets[this.symbol.symbolId as keyof typeof offsets];
            if (offsetForSymbol) {
                this.offset.x = offsetForSymbol.x;
                this.offset.y = offsetForSymbol.y;
            }
        }

        if (!spine.hasAnimation(animation)) {
            console.error("No animation: " + animation + " on symbol: " + this.symbol.symbolId + " " + this.symbol.gridPosition.x + "," + this.symbol.gridPosition.y);
            return Contract.empty();
        }

        Layers.SymbolAnimations.container.addChild(spine as any);
        this.updateTransform();

        return new Contract<void>((resolve) => {
            this.completeResolve = () => resolve(null);

            this.symbol.setVisible(false);
            spine.play(0, animation, loop);

            if (!loop) {
                spine.state.tracks[0].listener = {
                    complete: () => {
                        this.cleanSpine();

                        if (this.completeResolve) {
                            this.completeResolve();
                            this.completeResolve = null;
                        }
                    }
                }
            }
        });
    }

    protected cleanSpine() {
        this.symbol.setVisible(true);
        if (this.spine) {
            if (this.spine.state.tracks && this.spine.state.tracks[0] && this.spine.state.tracks[0].listener) {
                this.spine.state.tracks[0].listener.complete = null;
            }
            this.spine.state.setEmptyAnimation(0, 0);

            this.spine.state.clearTracks();
            this.spine.state.tracks = [];
            this.spine.skeleton.setToSetupPose();
            this.spine.state.timeScale = 1;

            this.symbol.setVisible(true);
            this.spine.visible = false;
            const spine = this.spine;
            this.spine = null;
            this.symbol.updateTransform();
            
            Layers.SymbolAnimations.container.removeChild(spine as any);
            spinePool.restore(spine.name, spine);
        }
    }

    protected cleanUp() {
        this.cleanSpine();

        this.completeResolve = null;
    }
}

const OFFSETS_LANDSCAPE = {
    "STAR": {x: 0, y: 8},
    "STRAWBERRY": {x: -8, y: 7},
    "PLUM": {x: -14, y: 0},
    "ORANGE": {x: -13, y: 2},
    "CHERRIES": {x: 3, y: -19},
    "LEMON": {x: -12, y: -9},
    "7": {x: -1, y: -3},
    "BELL": {x: 0, y: 3}
}

const OFFSET_PORTRAIT = {
    "STAR": {x: 0, y: 8},
    "PLUM": {x: -8, y: 4},
    "CHERRIES": {x: 7, y: -15},
    "ORANGE": {x: -8, y: -1},
    "7": {x: 0, y: -3},
    "BELL": {x: 0, y: 1}
}

const OFFSET_PORTRAITV2 = {
    "STAR": {x: -1, y: 4},
    "PLUM": {x: -6, y: 5},
    "ORANGE": {x: -5, y: 0},
    "CHERRIES": {x: 6, y: -14},
    "7": {x: 0, y: -2},
    "BELL": {x: 0, y: 3},
    "BAR": {x: 0, y: 1},
    "WILD": {x: 0, y: 1},
    "LEMON": {x: -12, y: -6},
}

